import { Box, Icon, SxProps, Typography } from "@outschool/backpack";
import { fasExclamationTriangle } from "@outschool/icons";
import {
  renderCurrencyString as renderBaseCurrencyString,
  useLocalPriceDisplay,
  useTranslation,
} from "@outschool/localization";
import React from "react";

// can define amountCents or amountCredits but not both
export type PriceDisplayProps = (AmountCentsOnly | AmountCreditsOnly) & {
  periodText?: string;
  dataTestId?: string;
  proratedAmountCents?: number;
  periodTextSx?: SxProps;
  showPlus?: boolean;
  showWarning?: boolean;
  useFree?: boolean;
};

type AmountCentsOnly = {
  amountCents: number | "-";
  amountCredits?: never;
};

type AmountCreditsOnly = {
  amountCredits: number;
  amountCents?: never;
};

const PRICE_SX: SxProps = {
  minWidth: "90px",
  textAlign: "right",
};

export function PriceDisplay({
  amountCents,
  amountCredits,
  periodText,
  dataTestId,
  proratedAmountCents,
  periodTextSx,
  showPlus = false,
  showWarning = false,
  useFree = true,
}: PriceDisplayProps) {
  const { isBaseCurrency, renderCurrencyString } = useLocalPriceDisplay();
  const { t } = useTranslation(
    "client\\components\\enrolling\\enrollmentForm\\priceBreakdown\\PriceDisplay",
  );

  function renderCurrencyStringWithUsdEquivalent(amountCentsInUsd: number) {
    const localCurrencyString = renderCurrencyString({
      priceInCents: amountCentsInUsd,
      useFree,
      showCurrencyCode: false,
    });
    if (amountCentsInUsd === 0 && useFree) {
      return <>{localCurrencyString}</>;
    } else {
      return (
        <>
          {localCurrencyString}{" "}
          <Typography
            variant="inherit"
            sx={{
              color: "grey.500",
            }}
          >
            ({showPlus && "+"}
            {renderBaseCurrencyString({
              priceInCents: amountCentsInUsd,
              useBaseCurrency: true,
              showCurrencyCode: false,
            })}
            )
          </Typography>
        </>
      );
    }
  }
  if (amountCents === "-") {
    return (
      <Box sx={PRICE_SX} data-test-id={dataTestId}>
        {amountCents}
      </Box>
    );
  }

  const totalAmountString = amountCredits
    ? t("{{count}} credit", {
        defaultValue_plural: "{{count}} credits",
        count: amountCredits,
      })
    : isBaseCurrency
    ? renderCurrencyString({
        priceInCents: amountCents!,
        useFree,
        showCurrencyCode: false,
      })
    : renderCurrencyStringWithUsdEquivalent(amountCents!);

  const proratedAmountString = !proratedAmountCents
    ? null
    : isBaseCurrency
    ? renderCurrencyString({
        priceInCents: proratedAmountCents,
        useFree,
        showCurrencyCode: false,
      })
    : renderCurrencyStringWithUsdEquivalent(proratedAmountCents);

  return (
    <Box sx={PRICE_SX} data-test-id={dataTestId}>
      {showWarning && (
        <>
          <Icon
            icon={fasExclamationTriangle}
            sx={{
              color: "#B28B1E",
            }}
          />{" "}
        </>
      )}
      {showPlus && "+"}
      {proratedAmountCents ? (
        <>
          {proratedAmountString} {t("prorated")}{" "}
          <Typography
            sx={{
              color: "grey.600",
              textDecoration: "line-through",
            }}
          >
            {totalAmountString}
          </Typography>
        </>
      ) : (
        totalAmountString
      )}
      {periodText && (
        <Typography variant="body1" sx={periodTextSx}>
          {" "}
          {periodText}
        </Typography>
      )}
    </Box>
  );
}
